import {
  LinearPrice,
  PackagedPrice,
  Price,
  Product
} from 'common/drawers/PricingEditor/domain'
import {
  Text,
  Badge,
  GreyGrey100,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  IconClipboard,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  Flex,
  Grid
} from '@sequencehq/core-components'
import kebabCase from 'lodash/kebabCase'
import {
  enforceMinimumPrecision,
  toBillingFrequencyLabel,
  toBillingType,
  toMoney,
  toPriceSummary
} from '@sequencehq/utils'
import {
  NEW_PRICE_PATTERN,
  NEW_PRODUCT_PATTERN,
  PRICING_TYPES_WITH_PERCENTAGE
} from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { match } from 'ts-pattern'
import { internalPriceToApiPrice } from 'common/drawers/PricingEditor/communication/external/v1/adapters/pricingEditor.adapters'
import capitalize from 'lodash/fp/capitalize'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useUsageMetrics } from '../../../../communication/external/useUsageMetrics'
import { PRICING_MODEL_OPTIONS } from '@sequencehq/utils'
import { toRevenueRecognitionMethodLabel } from 'modules/RevenueRecognition'

const pricingTypeLabel = (pricingModel: Price['common']['pricingModel']) => {
  return match(pricingModel)
    .with('SEAT_BASED_GRADUATED', 'SEAT_BASED_LINEAR', () => 'Seat based')
    .otherwise(() => {
      const option = PRICING_MODEL_OPTIONS.find(
        opt => opt.value === pricingModel
      )
      return option?.label ?? capitalize(kebabCase(pricingModel))
    })
}

const deriveIsPricePercentage = (price: Price): boolean => {
  if (
    price.common.pricingModel === 'VOLUME' &&
    'percentageTiers' in price.modelSpecific
  ) {
    return price.modelSpecific.usageTierType === 'PERCENTAGE'
  }

  if (
    price.common.pricingModel === 'GRADUATED' &&
    'percentageTiers' in price.modelSpecific
  ) {
    return price.modelSpecific.usageTierType === 'PERCENTAGE'
  }

  if (
    price.common.pricingModel === 'LINEAR' &&
    'linearPriceType' in price.modelSpecific
  ) {
    return price.modelSpecific.linearPriceType === 'PERCENTAGE'
  }

  return false
}

const LinearPricingFields = ({ price }: { price: LinearPrice }) => {
  return (
    <>
      {price.modelSpecific.linearPriceType === 'PERCENTAGE' ? (
        <>
          <Text {...Primary13Regular}>Percentage</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.percentage"
          >
            {enforceMinimumPrecision(2)(price.modelSpecific.percentage)}%
          </Text>
        </>
      ) : (
        <>
          <Text {...Primary13Regular}>Price per unit</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.pricePerUnit"
          >
            {toMoney({
              currency: price.common.currency,
              value: price.modelSpecific.price,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {price.modelSpecific.minPrice && (
        <>
          <Text {...Primary13Regular}>Min. Price</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.minPrice"
          >
            {toMoney({
              currency: price.common.currency,
              value: price.modelSpecific.minPrice,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {price.modelSpecific.maxPrice && (
        <>
          <Text {...Primary13Regular}>Max. Price</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.maxPrice"
          >
            {toMoney({
              currency: price.common.currency,
              value: price.modelSpecific.maxPrice,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
    </>
  )
}

const PackagePricingFields = ({ price }: { price: PackagedPrice }) => {
  return (
    <>
      {price.modelSpecific.pricePerPackage && (
        <>
          <Text {...Primary13Regular}>Price per package</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.pricePerPackage"
          >
            {toMoney({
              currency: price.common.currency,
              value: price.modelSpecific.pricePerPackage,
              maximumFractionDigits: 10
            })}
          </Text>
        </>
      )}
      {price.modelSpecific.packageSize && (
        <>
          <Text {...Primary13Regular}>Package size</Text>
          <Text
            {...Primary13Regular}
            color={GreyGrey90}
            data-testid="priceEditor.review.packageSize"
          >
            {price.modelSpecific.packageSize}
          </Text>
        </>
      )}
    </>
  )
}

const PriceUsageMetric = ({ usageMetricId }: { usageMetricId: string }) => {
  const { usageMetrics } = useUsageMetrics()
  const usageMetric = usageMetrics.find(metric => metric.id === usageMetricId)

  if (!usageMetric) {
    return null
  }

  const linkToUsageMetric = `/usage-metrics/${usageMetric.id}`

  return (
    <>
      <Text {...Primary13Regular}>Usage metric</Text>
      <Link.Unstyled to={linkToUsageMetric}>
        <Text
          {...Primary13SemiBold}
          color={IndigoIndigo60}
          data-testid="priceEditor.review.usageMetric"
        >
          {usageMetric.name}
        </Text>
      </Link.Unstyled>
    </>
  )
}

export const PriceMeta = ({
  price,
  product
}: {
  price: Price
  product?: Product
}) => {
  const handleCopy = () => {
    void navigator.clipboard.writeText(price.id)
  }

  const handleProductCopy = () => {
    if (!product) return
    void navigator.clipboard.writeText(product.id)
  }

  const { data: taxCategoriesData } = useQuery(
    dashboardv99990101Client.getTaxCategories
  )

  const taxCategoryForProduct = taxCategoriesData?.items?.find(
    taxCategory => taxCategory.id === product?.taxCategoryId
  )

  return (
    <Flex flexDirection="column">
      <Text {...Primary14SemiBold} color={GreyGrey100}>
        Details
      </Text>
      <Grid
        templateColumns={`${product?.revenueRecognitionMethod ? '176px' : '160px'} 1fr`}
        gap={3}
        mt={4}
        color={GreyGrey70}
      >
        {product && (
          <>
            <Text {...Primary13Regular}>Product name</Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="priceEditor.review.productName"
            >
              {product.name}
            </Text>

            {!product.id.match(NEW_PRODUCT_PATTERN) && (
              <>
                <Text {...Primary13Regular}>Product ID</Text>
                <Flex gap={2} data-testid="priceEditor.review.productId">
                  <Badge
                    sentiment="neutral"
                    containerStyle={{
                      height: '16px',
                      padding: 1
                    }}
                  >
                    {product.id}
                  </Badge>
                  <IconClipboard
                    size={14}
                    color={GreyGrey60}
                    onClick={handleProductCopy}
                    cursor="pointer"
                  />
                </Flex>
              </>
            )}

            {product.label && (
              <>
                <Text {...Primary13Regular}>Product description</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  whiteSpace="pre-line"
                  wordBreak="break-word"
                >
                  {product.label}
                </Text>
              </>
            )}

            {product.revenueRecognitionMethod && (
              <>
                <Text {...Primary13Regular}>Revenue recognition method</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  data-testid="priceEditor.review.revenueRecognitionMethod"
                >
                  {toRevenueRecognitionMethodLabel(
                    product.revenueRecognitionMethod
                  )}
                </Text>
              </>
            )}
            {taxCategoryForProduct && (
              <>
                <Text {...Primary13Regular}>Tax category</Text>
                <Text
                  {...Primary13Regular}
                  color={GreyGrey90}
                  data-testid="priceEditor.review.taxCategoryName"
                >
                  {taxCategoryForProduct.name}
                </Text>
              </>
            )}
          </>
        )}
        <Text {...Primary13Regular}>Pricing model</Text>
        <Text
          {...Primary13Regular}
          color={GreyGrey90}
          data-testid="priceEditor.review.pricingModel"
        >
          {`${pricingTypeLabel(price.common.pricingModel)} pricing`}
        </Text>
        <Text {...Primary13Regular}>Billing frequency</Text>
        <Text
          {...Primary13Regular}
          color={GreyGrey90}
          data-testid="priceEditor.review.billingFrequency"
        >
          {toBillingFrequencyLabel(price.modelSpecific.billingFrequency)} /{' '}
          {toBillingType(price.modelSpecific.billingType)}
        </Text>

        {'usageMetricId' in price.modelSpecific && (
          <PriceUsageMetric usageMetricId={price.modelSpecific.usageMetricId} />
        )}

        {PRICING_TYPES_WITH_PERCENTAGE.includes(price.common.pricingModel) && (
          <>
            <Text {...Primary13Regular}>Type</Text>
            <Text {...Primary13Regular} color={GreyGrey90}>
              {deriveIsPricePercentage(price) ? 'Percentage' : 'Fixed'}
            </Text>
          </>
        )}

        {price.common.pricingModel === 'LINEAR' ? (
          <LinearPricingFields price={price as LinearPrice} />
        ) : price.common.pricingModel === 'PACKAGE' ? (
          <PackagePricingFields price={price as PackagedPrice} />
        ) : (
          <>
            <Text {...Primary13Regular}>Price</Text>
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              data-testid="priceEditor.review.price"
            >
              {toPriceSummary({
                id: price.id,
                ...internalPriceToApiPrice(price.common.productId)(price)
              })}
            </Text>
          </>
        )}

        {!price.id.match(NEW_PRICE_PATTERN) && (
          <>
            <Text {...Primary13Regular}>Price ID</Text>
            <Flex gap={2}>
              <Badge
                sentiment="neutral"
                containerStyle={{
                  height: '16px',
                  padding: 1
                }}
              >
                {price.id}
              </Badge>
              <IconClipboard
                size={14}
                color={GreyGrey60}
                onClick={handleCopy}
                cursor="pointer"
              />
            </Flex>
          </>
        )}
      </Grid>
    </Flex>
  )
}
